export const PERMISSION_SCOPE = {
    /** Any municipality's data, and data that belongs to no municipality. Internal staff only. */
    GLOBAL: 'global',
    /**
     * Only the user's own municipality: `restrictTo` compares the route's `{municipalityId}` with
     * the user's, so a municipality permission only works on routes with that path parameter.
     */
    MUNICIPALITY: 'municipality',
};
// The tuples below come from their objects, so a new value can't be left out. `as const` doesn't
// apply to a call, so each is cast to the non-empty tuple pgEnum and z.enum require.
export const PERMISSION_SCOPES = Object.values(PERMISSION_SCOPE);
/** Separates the parts of `<resource>:<verb>:<scope>`. */
export const PERMISSION_ACTION_SEPARATOR = ':';
/**
 * Permissions for internal staff: they work on any municipality's data, so only global roles
 * hold them. Each comment names what the permission lets its holder do.
 */
export const GLOBAL_PERMISSION_ACTION = {
    /** List municipalities, and read any one of them with its data source settings. */
    MUNICIPALITIES_READ_GLOBAL: `municipalities:read:${PERMISSION_SCOPE.GLOBAL}`,
    /** Create municipalities. */
    MUNICIPALITIES_CREATE_GLOBAL: `municipalities:create:${PERMISSION_SCOPE.GLOBAL}`,
    /** Change any municipality's settings, such as which data sources it uses. */
    MUNICIPALITIES_UPDATE_GLOBAL: `municipalities:update:${PERMISSION_SCOPE.GLOBAL}`,
    /** Delete a municipality, and with it everything that belongs to it. */
    MUNICIPALITIES_DELETE_GLOBAL: `municipalities:delete:${PERMISSION_SCOPE.GLOBAL}`,
    /** Read any municipality's geo fence. */
    GEO_FENCES_READ_GLOBAL: `geo-fences:read:${PERMISSION_SCOPE.GLOBAL}`,
    /** Give a municipality without one a geo fence. Either this or update allows the upsert. */
    GEO_FENCES_CREATE_GLOBAL: `geo-fences:create:${PERMISSION_SCOPE.GLOBAL}`,
    /** Replace any municipality's geo fence. Either this or create allows the upsert. */
    GEO_FENCES_UPDATE_GLOBAL: `geo-fences:update:${PERMISSION_SCOPE.GLOBAL}`,
    /** Remove any municipality's geo fence. */
    GEO_FENCES_DELETE_GLOBAL: `geo-fences:delete:${PERMISSION_SCOPE.GLOBAL}`,
    /**
     * Read any municipality's road network: segments, summary, attribute corrections, and the
     * status and logs of road segment and attribute import jobs.
     */
    ROAD_SEGMENTS_READ_GLOBAL: `road-segments:read:${PERMISSION_SCOPE.GLOBAL}`,
    /** Start and cancel road network imports from OSM for any municipality. */
    ROAD_SEGMENTS_IMPORT_GLOBAL: `road-segments:import:${PERMISSION_SCOPE.GLOBAL}`,
    /**
     * Start and cancel road segment attribute imports for any municipality, and add or remove
     * manual attribute corrections.
     */
    ROAD_SEGMENT_ATTRIBUTES_IMPORT_GLOBAL: `road-segment-attributes:import:${PERMISSION_SCOPE.GLOBAL}`,
    /**
     * Read any municipality's points of interest: the stored places, the hand corrections over
     * them, and the status and logs of places import jobs.
     */
    POINTS_OF_INTEREST_READ_GLOBAL: `points-of-interest:read:${PERMISSION_SCOPE.GLOBAL}`,
    /**
     * Start and cancel points of interest imports for any municipality, and add or remove the hand
     * corrections that put a place in or take one out.
     */
    POINTS_OF_INTEREST_IMPORT_GLOBAL: `points-of-interest:import:${PERMISSION_SCOPE.GLOBAL}`,
    /**
     * Read any municipality's population: the stored cells and the status and logs of population
     * import jobs.
     */
    POPULATION_READ_GLOBAL: `population:read:${PERMISSION_SCOPE.GLOBAL}`,
    /** Start and cancel population imports for any municipality. */
    POPULATION_IMPORT_GLOBAL: `population:import:${PERMISSION_SCOPE.GLOBAL}`,
    /**
     * Read any municipality's land use: the stored zones and the status and logs of land use
     * import jobs.
     */
    LAND_USE_ZONES_READ_GLOBAL: `land-use-zones:read:${PERMISSION_SCOPE.GLOBAL}`,
    /** Start and cancel land use imports for any municipality. */
    LAND_USE_ZONES_IMPORT_GLOBAL: `land-use-zones:import:${PERMISSION_SCOPE.GLOBAL}`,
    /**
     * Read any municipality's buildings: the stored buildings with their estimated residents, and
     * the status and logs of buildings import jobs.
     */
    BUILDINGS_READ_GLOBAL: `buildings:read:${PERMISSION_SCOPE.GLOBAL}`,
    /** Start and cancel buildings imports for any municipality. */
    BUILDINGS_IMPORT_GLOBAL: `buildings:import:${PERMISSION_SCOPE.GLOBAL}`,
    /** List the whole permission catalogue, and read any municipality's available permissions. */
    PERMISSIONS_READ_GLOBAL: `permissions:read:${PERMISSION_SCOPE.GLOBAL}`,
    /**
     * Choose which municipality permissions a municipality may use, by hand or through packages.
     * This is the ceiling for every role in that municipality, so only internal staff hold it.
     */
    PERMISSIONS_UPDATE_GLOBAL: `permissions:update:${PERMISSION_SCOPE.GLOBAL}`,
    /**
     * Choose which permissions a role holds, in any role: needed to create a role with permissions
     * or to change a role's permissions. Only what the holder has themselves can be granted.
     */
    PERMISSIONS_GRANT_GLOBAL: `permissions:grant:${PERMISSION_SCOPE.GLOBAL}`,
    /** List and read global roles and every municipality's roles. */
    ROLES_READ_GLOBAL: `roles:read:${PERMISSION_SCOPE.GLOBAL}`,
    /** Create global roles, and roles in any municipality. Giving them permissions needs grant. */
    ROLES_CREATE_GLOBAL: `roles:create:${PERMISSION_SCOPE.GLOBAL}`,
    /** Rename any role. Changing its permissions needs `permissions:grant:global`. */
    ROLES_UPDATE_GLOBAL: `roles:update:${PERMISSION_SCOPE.GLOBAL}`,
    /** Delete any role that no user holds. */
    ROLES_DELETE_GLOBAL: `roles:delete:${PERMISSION_SCOPE.GLOBAL}`,
    /** List and read every user, global and municipality ones. */
    USERS_READ_GLOBAL: `users:read:${PERMISSION_SCOPE.GLOBAL}`,
    /** Change any user's role and deactivate or reactivate them. */
    USERS_UPDATE_GLOBAL: `users:update:${PERMISSION_SCOPE.GLOBAL}`,
    /** List every invite, global and municipality ones, with whether it is pending or accepted. */
    INVITES_READ_GLOBAL: `invites:read:${PERMISSION_SCOPE.GLOBAL}`,
    /** Invite global users and users into any municipality. */
    INVITES_CREATE_GLOBAL: `invites:create:${PERMISSION_SCOPE.GLOBAL}`,
    /** Resend any open invite, which renews its link and expiry. */
    INVITES_UPDATE_GLOBAL: `invites:update:${PERMISSION_SCOPE.GLOBAL}`,
    /** Revoke any invite that hasn't been accepted yet. */
    INVITES_DELETE_GLOBAL: `invites:delete:${PERMISSION_SCOPE.GLOBAL}`,
    /** List the feature flags with their stage, rollout and the municipalities ticked for them. */
    FEATURE_FLAGS_READ_GLOBAL: `feature-flags:read:${PERMISSION_SCOPE.GLOBAL}`,
    /**
     * Set who a feature flag is on for. Turning one off also takes its permissions off the roles of
     * the municipalities that lose it.
     */
    FEATURE_FLAGS_UPDATE_GLOBAL: `feature-flags:update:${PERMISSION_SCOPE.GLOBAL}`,
    /** List and read the countries and their settings (coordinate system, time zone, speed limits). */
    COUNTRIES_READ_GLOBAL: `countries:read:${PERMISSION_SCOPE.GLOBAL}`,
    /** Add a country, so municipalities can be created in it. */
    COUNTRIES_CREATE_GLOBAL: `countries:create:${PERMISSION_SCOPE.GLOBAL}`,
    /**
     * Change a country's settings. Every municipality in it reads them, so a change reaches all of
     * them on their next import.
     */
    COUNTRIES_UPDATE_GLOBAL: `countries:update:${PERMISSION_SCOPE.GLOBAL}`,
    /** Delete a country that no municipality is in. */
    COUNTRIES_DELETE_GLOBAL: `countries:delete:${PERMISSION_SCOPE.GLOBAL}`,
};
/**
 * Permissions for customers: they only work inside the holder's own municipality. A municipality
 * can be given these to build its own roles from; it never gets a global one.
 */
export const MUNICIPALITY_PERMISSION_ACTION = {
    /** Read the user's own municipality. No route accepts it yet. */
    MUNICIPALITIES_READ_MUNICIPALITY: `municipalities:read:${PERMISSION_SCOPE.MUNICIPALITY}`,
    /** Read the permissions available to the user's own municipality, to build roles from them. */
    PERMISSIONS_READ_MUNICIPALITY: `permissions:read:${PERMISSION_SCOPE.MUNICIPALITY}`,
    /**
     * Choose which permissions the own municipality's roles hold, from what the municipality may
     * use and only what the holder has themselves.
     */
    PERMISSIONS_GRANT_MUNICIPALITY: `permissions:grant:${PERMISSION_SCOPE.MUNICIPALITY}`,
    /** List and read the roles of the user's own municipality. */
    ROLES_READ_MUNICIPALITY: `roles:read:${PERMISSION_SCOPE.MUNICIPALITY}`,
    /** Create roles in the user's own municipality. Giving them permissions needs grant. */
    ROLES_CREATE_MUNICIPALITY: `roles:create:${PERMISSION_SCOPE.MUNICIPALITY}`,
    /** Rename the own municipality's roles. Changing their permissions needs grant. */
    ROLES_UPDATE_MUNICIPALITY: `roles:update:${PERMISSION_SCOPE.MUNICIPALITY}`,
    /** Delete the own municipality's roles that no user holds. */
    ROLES_DELETE_MUNICIPALITY: `roles:delete:${PERMISSION_SCOPE.MUNICIPALITY}`,
    /** List and read the users of the user's own municipality. */
    USERS_READ_MUNICIPALITY: `users:read:${PERMISSION_SCOPE.MUNICIPALITY}`,
    /** Change the role of the own municipality's users, and deactivate or reactivate them. */
    USERS_UPDATE_MUNICIPALITY: `users:update:${PERMISSION_SCOPE.MUNICIPALITY}`,
    /** List the own municipality's invites, with whether each is pending or accepted. */
    INVITES_READ_MUNICIPALITY: `invites:read:${PERMISSION_SCOPE.MUNICIPALITY}`,
    /** Invite coworkers into the user's own municipality, with one of its roles. */
    INVITES_CREATE_MUNICIPALITY: `invites:create:${PERMISSION_SCOPE.MUNICIPALITY}`,
    /** Resend the own municipality's open invites, which renews their link and expiry. */
    INVITES_UPDATE_MUNICIPALITY: `invites:update:${PERMISSION_SCOPE.MUNICIPALITY}`,
    /** Revoke the own municipality's invites that haven't been accepted yet. */
    INVITES_DELETE_MUNICIPALITY: `invites:delete:${PERMISSION_SCOPE.MUNICIPALITY}`,
};
/** Every permission a role can hold, as `<resource>:<verb>:<scope>`; routes list them in `access`. */
export const PERMISSION_ACTION = {
    ...GLOBAL_PERMISSION_ACTION,
    ...MUNICIPALITY_PERMISSION_ACTION,
};
export const PERMISSION_ACTIONS = Object.values(PERMISSION_ACTION);
/**
 * The verbs each verb needs alongside it on the same resource and scope. Every verb needs read,
 * since no screen can act on what it can't list; create, update and delete stay independent of
 * each other. Typed over every verb, so a new one must say what it needs.
 */
export const PERMISSION_VERB_REQUIRES = {
    read: [],
    create: ['read'],
    update: ['read'],
    delete: ['read'],
    import: ['read'],
    grant: ['read'],
};
